/* 维护边界：独立购买区控制器；不注册或覆盖 Impact 的 product-form / variant-picker。 */
(() => {
  if (customElements.get('bixdo-product')) return;
  class BixdoProduct extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.state = JSON.parse(this.querySelector('[data-bixdo-state]').textContent);
      this.copy = JSON.parse(this.querySelector('[data-bixdo-config]').textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.root = window.Shopify?.routes?.root || '/';
      this.busy ??= false;
      this.loading = false;
      this.failedVariant ??= false;
      this.uncertain ??= false;
      this.sequence ??= 0;
      // 缓存仅限本实例、当前币种，30 秒过期；失败响应不缓存。
      this.variantCache = new Map();
      this.galleryVariant = null;
      const options = { signal: this.abort.signal };
      this.form.addEventListener('submit', event => this.add(event), options);
      this.addEventListener('change', event => this.change(event), options);
      this.addEventListener('pointerover', event => this.prefetchOption(event), options);
      this.addEventListener('focusin', event => this.prefetchOption(event), options);
      this.querySelectorAll('[data-bixdo-enhanced]').forEach(node => { node.hidden = false; });
      this.normalizeLinks();
      this.syncOptionVisibility();
      this.update();
      this.scheduleGiftExpiry();
      this.syncGallery();
      this.schedulePrefetch();
      this.querySelector('[data-bixdo-fallback]').hidden = true;
      const sticky = this.querySelector('[data-bixdo-sticky]');
      const button = this.form.querySelector('[data-bixdo-submit]');
      if (sticky && button) {
        const refreshSticky = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => {
            this.stickyFrame = null;
            sticky.hidden = button.getBoundingClientRect().bottom > 0;
          });
        };
        // 锚点跳转可能从不相交直接跳到不相交；不能只依赖交叉阈值。
        window.addEventListener('scroll', refreshSticky, { ...options, passive: true });
        window.addEventListener('resize', refreshSticky, options);
        window.addEventListener('pageshow', refreshSticky, options);
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(refreshSticky);
          this.observer.observe(button);
        }
        refreshSticky();
      }
    }

    disconnectedCallback() {
      // 旧请求不能影响重新挂载后的生命周期；未完成选择保持禁止购买。
      this.sequence = (this.sequence || 0) + 1;
      if (this.loading) this.failedVariant = true;
      this.loading = false;
      this.abort?.abort();
      this.variantCache?.forEach(entry => entry.controller.abort());
      this.variantCache?.clear();
      clearTimeout(this.prefetchTimer);
      window.cancelIdleCallback?.(this.prefetchIdle);
      cancelAnimationFrame(this.galleryFrame);
      this.observer?.disconnect();
      clearTimeout(this.giftExpiry);
      cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.abort = null;
    }

    normalizeLinks() {
      this.querySelectorAll('a[href]').forEach(link => {
        const url = new URL(link.href, location.href);
        if (/^https?:$/.test(url.protocol) && url.origin !== location.origin) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        } else {
          link.removeAttribute('target');
        }
      });
    }

    syncGallery() {
      const sequence = this.sequence;
      const signal = this.abort.signal;
      cancelAnimationFrame(this.galleryFrame);
      // 等原生图库完成注册后只派发一次事件；不再额外滚动三次。
      Promise.all(['product-gallery', 'media-carousel'].map(name => customElements.whenDefined(name))).then(() => {
        if (signal.aborted || sequence !== this.sequence || !this.isConnected) return;
        this.galleryFrame = requestAnimationFrame(() => {
          if (signal.aborted || sequence !== this.sequence || !this.isConnected) return;
          // 连续选择可能跳过中间帧；前一个变体必须是图库实际收到的那个。
          this.form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: this.state.variant, previousVariant: this.galleryVariant || {}, formId: this.form.id } }));
          this.galleryVariant = this.state.variant;
        });
      });
    }

    optionValues(candidate) {
      return [...this.querySelectorAll('[data-bixdo-option]:checked, input[type="hidden"][data-bixdo-option]')]
        .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
        .map(input => candidate?.dataset.position === input.dataset.position ? candidate.value : input.value);
    }

    variantMatrix() {
      try { return JSON.parse(this.querySelector('[data-bixdo-variant-matrix]')?.textContent || 'null'); }
      catch { return null; }
    }

    optionDependsOn(dependentIndex, controllerIndex, matrix = this.variantMatrix()) {
      if (!matrix?.variants?.length || dependentIndex === controllerIndex) return false;
      const controllersByDependent = new Map();
      const dependentsByController = new Map();
      matrix.variants.forEach(variant => {
        const dependentValue = variant.options[dependentIndex];
        const controllerValue = variant.options[controllerIndex];
        if (dependentValue == null || controllerValue == null) return;
        if (!controllersByDependent.has(dependentValue)) controllersByDependent.set(dependentValue, new Set());
        if (!dependentsByController.has(controllerValue)) dependentsByController.set(controllerValue, new Set());
        controllersByDependent.get(dependentValue).add(controllerValue);
        dependentsByController.get(controllerValue).add(dependentValue);
      });
      return controllersByDependent.size > 0 &&
        [...controllersByDependent.values()].every(values => values.size === 1) &&
        [...dependentsByController.values()].some(values => values.size > 1);
    }

    dependentPositions(controllerIndex, matrix = this.variantMatrix()) {
      const optionCount = matrix?.options?.length || matrix?.variants?.[0]?.options?.length || 0;
      const dependents = new Set();
      const controllers = [controllerIndex];
      for (let sourceIndex = 0; sourceIndex < controllers.length; sourceIndex += 1) {
        const sourcePosition = controllers[sourceIndex];
        for (let candidatePosition = 0; candidatePosition < optionCount; candidatePosition += 1) {
          if (candidatePosition === controllerIndex || dependents.has(candidatePosition)) continue;
          if (this.optionDependsOn(candidatePosition, sourcePosition, matrix)) {
            dependents.add(candidatePosition);
            controllers.push(candidatePosition);
          }
        }
      }
      return dependents;
    }

    resolvedOptionValues(candidate) {
      const values = this.optionValues(candidate);
      const matrix = this.variantMatrix();
      const changedPosition = Number(candidate?.dataset.position);
      if (!matrix?.variants?.length || !changedPosition || !candidate?.value) return values;
      const changedIndex = changedPosition - 1;
      const selected = new Map(values.map((value, index) => [index, value]));
      selected.set(changedIndex, candidate.value);
      const dependents = this.dependentPositions(changedIndex, matrix);
      return matrix.variants.filter(variant => variant.options[changedIndex] === candidate.value).reduce((best, variant) => {
        const stableScore = variant.options.reduce((score, value, index) => score + (index !== changedIndex && !dependents.has(index) && selected.get(index) === value ? 1 : 0), 0);
        const totalScore = variant.options.reduce((score, value, index) => score + (selected.get(index) === value ? 1 : 0), 0);
        if (!best || stableScore > best.stableScore || stableScore === best.stableScore && totalScore > best.totalScore) return { variant, stableScore, totalScore };
        return best;
      }, null)?.variant.options || values;
    }

    removeOptionControl(input) {
      const label = input.closest('label') || (input.id ? this.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
      input.remove();
      label?.remove();
    }

    syncOptionVisibility() {
      const matrix = this.variantMatrix();
      if (!matrix?.variants?.length) return;
      const selectedValues = [];
      this.querySelectorAll('[data-bixdo-option]:checked, input[type="hidden"][data-bixdo-option]').forEach(input => {
        selectedValues[Number(input.dataset.position) - 1] = input.value;
      });
      const optionCount = matrix.options?.length || matrix.variants[0]?.options?.length || 0;
      this.querySelectorAll('[data-bixdo-option]').forEach(input => {
        const optionIndex = Number(input.dataset.position) - 1;
        const controllers = Array.from({ length: optionCount }, (_, index) => index).filter(index => this.optionDependsOn(optionIndex, index, matrix));
        const exists = matrix.variants.some(variant => variant.options[optionIndex] === input.value && controllers.every(index => !selectedValues[index] || variant.options[index] === selectedValues[index]));
        if (!exists) this.removeOptionControl(input);
      });
    }

    variantURL(values) {
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('option_values', values.join(','));
      return url.href;
    }

    requestVariant(values) {
      const url = this.variantURL(values);
      const key = `${this.state.currency}:${url}`;
      const cached = this.variantCache.get(key);
      if (cached && (!cached.completedAt || Date.now() - cached.completedAt < 30000)) return cached.promise;
      this.variantCache.delete(key);
      // 限制内存和并发；切换仅由 sequence 决定，旧响应不能覆盖新选择。
      if (this.variantCache.size >= 12) {
        const oldest = this.variantCache.keys().next().value;
        this.variantCache.get(oldest).controller.abort();
        this.variantCache.delete(oldest);
      }
      const controller = new AbortController();
      const entry = { controller, completedAt: 0 };
      const timeout = setTimeout(() => controller.abort(), 15000);
      entry.promise = (async () => {
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Variant request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const replacement = html.getElementById(this.id);
          if (!replacement?.querySelector('[data-bixdo-state]')) throw new Error('Missing section');
          const next = JSON.parse(replacement.querySelector('[data-bixdo-state]').textContent);
          entry.completedAt = Date.now();
          return { html, replacement, next };
        } catch (error) {
          if (this.variantCache.get(key) === entry) this.variantCache.delete(key);
          throw error;
        } finally {
          clearTimeout(timeout);
        }
      })();
      this.variantCache.set(key, entry);
      return entry.promise;
    }

    canPrefetch(input) {
      const connection = navigator.connection;
      return !this.busy && !this.loading && !this.uncertain && !window.Shopify?.designMode &&
        !connection?.saveData && !/2g/.test(connection?.effectiveType || '') &&
        input && !input.checked && !input.disabled && input.type !== 'hidden' &&
        (!input.dataset.productUrl || new URL(input.dataset.productUrl, location.href).pathname === new URL(this.dataset.productUrl, location.href).pathname);
    }

    prefetchOption(event) {
      const label = event.target.closest('label');
      const input = event.target.matches('[data-bixdo-option]') ? event.target : label?.control;
      if (!input?.matches('[data-bixdo-option]') || !this.canPrefetch(input)) return;
      this.requestVariant(this.resolvedOptionValues(input)).catch(() => {});
    }

    schedulePrefetch() {
      clearTimeout(this.prefetchTimer);
      window.cancelIdleCallback?.(this.prefetchIdle);
      const signal = this.abort.signal;
      // 后台串行最多预取四个邻近组合，避免全量组合下载影响首屏。
      const prefetch = async () => {
        const sequence = this.sequence;
        const inputs = [...this.querySelectorAll('[data-bixdo-option]')].filter(input => this.canPrefetch(input)).slice(0, 4);
        for (const input of inputs) {
          if (signal.aborted || sequence !== this.sequence || !this.isConnected || !this.canPrefetch(input)) break;
          try { await this.requestVariant(this.resolvedOptionValues(input)); } catch { /* 预取失败由实际选择重试。 */ }
        }
      };
      this.prefetchTimer = setTimeout(() => {
        if (signal.aborted) return;
        if (window.requestIdleCallback) this.prefetchIdle = window.requestIdleCallback(prefetch, { timeout: 2000 });
        else prefetch();
      }, 1200);
    }

    replaceChangedBlocks(replacement) {
      let paymentInserted = false;
      this.querySelectorAll('[data-bixdo-replace]').forEach(node => {
        const match = replacement.querySelector(`[data-bixdo-replace="${CSS.escape(node.dataset.bixdoReplace)}"]`);
        if (!match || node.isEqualNode(match)) return;
        const clone = match.cloneNode(true);
        // 保留规格控件节点和焦点；结构改变时才使用服务端完整块。
        if (node.classList.contains('bixdo-product__options')) {
          const before = node.cloneNode(true);
          const after = match.cloneNode(true);
          [before, after].forEach(root => {
            root.querySelectorAll('[data-bixdo-option]').forEach(input => input.removeAttribute('checked'));
            root.querySelector('.bixdo-product__bundle-details')?.remove();
          });
          if (before.isEqualNode(after)) {
            const incoming = [...match.querySelectorAll('[data-bixdo-option]')];
            node.querySelectorAll('[data-bixdo-option]').forEach((input, index) => {
              input.checked = incoming[index].checked;
              input.defaultChecked = incoming[index].defaultChecked;
            });
            const details = node.querySelector('.bixdo-product__bundle-details');
            const nextDetails = clone.querySelector('.bixdo-product__bundle-details');
            if (details && nextDetails) { nextDetails.open = details.open; if (!details.isEqualNode(nextDetails)) details.replaceWith(nextDetails); }
            else if (details) details.remove();
            else if (nextDetails) node.append(nextDetails);
            return;
          }
        }
        paymentInserted ||= !!clone.querySelector('.shopify-payment-button, shopify-accelerated-checkout');
        node.replaceWith(clone);
      });
      return paymentInserted;
    }

    change(event) {
      if (event.target.matches('[data-bixdo-option]')) {
        if (event.target.dataset.productUrl) {
          const url = new URL(event.target.dataset.productUrl, location.href);
          if (url.pathname !== new URL(this.dataset.productUrl, location.href).pathname) {
            location.assign(url.href);
            return;
          }
        }
        this.loadVariant(this.resolvedOptionValues(event.target));
      } else if (event.target.matches('[data-bixdo-include], [data-bixdo-accessory-variant]')) {
        this.update();
      }
    }

    accessoryVariant(row) {
      const select = row.querySelector('select[data-bixdo-accessory-variant]');
      return select?.selectedOptions[0] || row.querySelector('input[type="hidden"][data-bixdo-accessory-variant]');
    }

    scheduleGiftExpiry() {
      clearTimeout(this.giftExpiry);
      const remaining = (this.state.promotion?.endsAt || 0) * 1000 - Date.now();
      if (remaining > 0) this.giftExpiry = setTimeout(() => {
        if (!this.isConnected) return;
        this.update();
        this.scheduleGiftExpiry();
      }, Math.min(remaining + 50, 2147483647));
    }

    selectedItems() {
      const items = new Map();
      if (this.state.variant?.id) items.set(String(this.state.variant.id), { id: this.state.variant.id, quantity: 1, price: this.state.variant.price, compareAt: this.state.variant.compare_at_price, kind: 'main' });
      this.querySelectorAll('[data-bixdo-accessory]').forEach(row => {
        const check = row.querySelector('[data-bixdo-include]');
        const option = this.accessoryVariant(row);
        if (!check?.checked || option?.dataset.available !== 'true') return;
        // 同一 SKU 在当前选择内仅计一次；不改变购物车原有数量。
        if (!items.has(option.value)) items.set(option.value, { id: Number(option.value), quantity: 1, price: Number(option.dataset.price), compareAt: Number(option.dataset.compareAt), kind: 'accessory' });
      });
      return [...items.values(), ...(window.BixdoGiftEngine?.additions(this.state.variant?.id, this.state.promotion) || []).map(item => ({ ...item, price: 0, compareAt: 0, kind: 'gift' }))];
    }

    update() {
      const locked = this.busy || this.loading || this.uncertain;
      this.querySelectorAll('[data-bixdo-accessory]').forEach(row => {
        const option = this.accessoryVariant(row);
        const check = row.querySelector('[data-bixdo-include]');
        if (!option || !check) return;
        if (option.dataset.available !== 'true') check.checked = false;
        check.disabled = locked || option.dataset.available !== 'true';
        const select = row.querySelector('select[data-bixdo-accessory-variant]');
        if (select) select.disabled = locked;
        row.classList.toggle('is-selected', check.checked);
        row.querySelector('[data-bixdo-accessory-price]').textContent = option.dataset.money;
        const image = row.querySelector('img');
        if (image && option.dataset.image && image.src !== option.dataset.image) {
          image.removeAttribute('srcset');
          image.src = option.dataset.image;
        }
      });
      const gifts = window.BixdoGiftEngine?.gifts(this.state.promotion) || [];
      const extras = !!this.querySelector('[data-bixdo-include]:checked') || gifts.length > 0;
      const giftCount = gifts.reduce((sum, item) => sum + item.quantity, 0);
      this.querySelectorAll('[data-bixdo-sticky-gifts]').forEach(node => {
        node.textContent = giftCount ? `${node.dataset.template.replace('{count}', giftCount)}${node.nextElementSibling ? ' · ' : ''}` : '';
      });
      // Gift badges describe qualifying option values, even when that value is not selected.
      // Only the detail list follows the currently selected Variant's promotion.
      this.querySelectorAll('.bixdo-product__gifts').forEach(node => { node.hidden = !giftCount; });
      const allowed = this.state.purchasable && !this.failedVariant;
      this.querySelectorAll('[data-bixdo-submit]').forEach(button => {
        button.disabled = locked || !allowed;
        button.setAttribute('aria-busy', String(this.busy || this.loading));
        button.textContent = this.busy || this.loading ? this.copy.loading : allowed ? this.copy.add : this.state.variant?.available === false ? this.copy.soldOut : this.copy.unavailable;
      });
      // 加购时冻结选项；规格请求期间仍可继续选择，最后一次响应才生效。
      this.querySelectorAll('[data-bixdo-option]').forEach(input => { input.disabled = this.busy || this.uncertain; });
      this.querySelectorAll('[data-bixdo-payment]').forEach(node => { node.hidden = extras || locked || !allowed; });
      this.querySelectorAll('[data-bixdo-payment-notice]').forEach(node => { node.hidden = !extras; });
      const selected = this.selectedItems();
      const cents = selected.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.state.currency, currencyDisplay: 'code' }).format(cents / 100);
      this.querySelectorAll('[data-bixdo-total]').forEach(node => { node.textContent = this.state.variant ? money : '—'; });
      // 仅扩充待购价格展示；提交仍由原有清单构建，赠品不计入节省金额。
      const compareTotal = selected.reduce((sum, item) => sum + (Number.isFinite(Number(item.compareAt)) && Number(item.compareAt) > item.price ? Number(item.compareAt) : item.price) * item.quantity, 0);
      this.querySelectorAll('[data-bixdo-compare-total]').forEach(node => {
        node.hidden = !this.state.variant || compareTotal <= cents;
        node.textContent = node.hidden ? '' : new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.state.currency, currencyDisplay: 'code' }).format(compareTotal / 100);
      });
      const accessoryCount = selected.filter(item => item.kind === 'accessory').reduce((sum, item) => sum + item.quantity, 0);
      this.querySelectorAll('[data-bixdo-sticky-accessories]').forEach(node => {
        const template = accessoryCount === 1 ? node.dataset.single : node.dataset.plural;
        node.textContent = template ? template.replace('{count}', accessoryCount) : '';
        node.hidden = !accessoryCount || !template;
      });
    }

    status(message, showCart = false) {
      this.querySelector('[data-bixdo-status]').textContent = message;
      this.querySelector('[data-bixdo-cart-link]').hidden = !showCart;
    }

    async loadVariant(resolvedValues) {
      const sequence = ++this.sequence;
      cancelAnimationFrame(this.galleryFrame);
      this.loading = true;
      this.failedVariant = false;
      this.status('');
      this.update();
      const values = resolvedValues || this.optionValues();
      try {
        const { html, replacement, next } = await this.requestVariant(values);
        if (sequence !== this.sequence || !this.isConnected) return;
        const focusId = this.contains(document.activeElement) ? document.activeElement.id : null;
        const paymentInserted = this.replaceChangedBlocks(replacement);
        this.state = next;
        this.scheduleGiftExpiry();
        this.querySelectorAll('[data-bixdo-enhanced]').forEach(node => { node.hidden = false; });
        this.syncOptionVisibility();
        this.querySelector('[data-bixdo-state]').textContent = JSON.stringify(next);
        this.form.elements.namedItem('id').value = next.variant?.id || '';
        this.form.elements.namedItem('id').dispatchEvent(new Event('change', { bubbles: true }));
        const currentURL = new URL(location.href);
        if (next.variant?.id) {
          currentURL.searchParams.set('variant', next.variant.id);
          currentURL.searchParams.delete('option_values');
        } else {
          currentURL.searchParams.delete('variant');
          currentURL.searchParams.set('option_values', values.join(','));
        }
        history.replaceState({}, '', currentURL);
        // 原生图库内部的数组比较会始终触发过滤；只有过滤列表变化才通知。
        const gallery = this.querySelector('product-gallery');
        const nextGallery = replacement.querySelector('product-gallery');
        if (gallery && nextGallery && gallery.getAttribute('filtered-indexes') !== nextGallery.getAttribute('filtered-indexes')) {
          this.form.dispatchEvent(new CustomEvent('product:rerender', { detail: { htmlFragment: html, productChange: false } }));
        }
        // Impact 的图库需要完整 featured_media 与前一个变体。
        this.syncGallery();
        if (focusId) this.querySelector(`#${CSS.escape(focusId)}`)?.focus({ preventScroll: true });
        // 主购买表单未被替换，无须每次重新初始化快捷支付。
        if (paymentInserted) window.Shopify?.PaymentButton?.init();
      } catch (error) {
        if (sequence === this.sequence && this.isConnected) {
          this.failedVariant = true;
          this.status(this.copy.variantError);
        }
      } finally {
        if (sequence === this.sequence && this.isConnected) { this.loading = false; this.update(); this.schedulePrefetch(); }
      }
    }

    async add(event) {
      event.preventDefault();
      if (this.adding || this.busy || this.loading || this.failedVariant || this.uncertain || !this.state.purchasable) return;
      this.adding = true;
      this.busy = true;
      this.update();
      try {
        const run = () => { this.busy = false; return this.performAdd(); };
        if (window.BixdoGifts) await window.BixdoGifts.exclusive(run);
        else if (!this.state.promotion?.gifts?.length) await run();
        else this.status(this.copy.error, true);
      } catch (error) {
        this.status(this.copy.cartRefresh, true);
      } finally { this.adding = false; this.busy = false; if (this.isConnected) this.update(); }
    }

    async performAdd() {
      if (this.busy || this.loading || this.failedVariant || this.uncertain || !this.state.purchasable || !this.form.reportValidity()) return;
      this.busy = true;
      this.status('');
      this.update();
      const sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const data = new FormData(this.form);
      const properties = {};
      for (const [key, value] of data.entries()) if (/^properties\[.+\]$/.test(key)) properties[key.slice(11, -1)] = value;
      const items = this.selectedItems().map(({ id, quantity, properties: giftProperties }, index) => ({ id, quantity, ...(giftProperties ? { properties: giftProperties } : {}), ...(index === 0 ? { properties, ...(data.get('selling_plan') ? { selling_plan: data.get('selling_plan') } : {}) } : {}) }));
      const requestAbort = new AbortController();
      const timeout = setTimeout(() => requestAbort.abort(), 20000);
      let confirmed = false;
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', signal: requestAbort.signal,
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: [...new Set(sections)].slice(0, 5), sections_url: `${this.root}variants/${this.state.variant.id}` })
        });
        const result = await response.json();
        if (!response.ok) {
          // 拒绝响应也不假定批量操作具备事务回滚；提示先检查购物车。
          this.status(this.copy.error, true);
          if (items.some(item => item.properties?._bixdo_gift === 'true')) this.uncertain = true;
          if (this.uncertain) window.BixdoGifts?.sync().catch(() => {});
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        confirmed = true;
        const cartResponse = await fetch(`${this.root}cart.js`, { signal: requestAbort.signal });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        let cart = await cartResponse.json();
        cart.sections = result.sections || {};
        if (window.themeVariables?.settings?.cartType === 'page') { location.assign(`${this.root}cart`); return; }
        if ([...new Set(sections)].some(id => !cart.sections[id])) throw new Error('Cart section unavailable');
        this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: result.items || [result], cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', bixdoGiftSync: true, cart } }));
        // Open Impact's success UI before optional relationship synchronization.
        // A snapshot/network failure must not turn a confirmed add into a failure.
        if (window.BixdoGifts && (this.state.promotion?.gifts?.length || cart.items?.some(window.BixdoGiftEngine.isGift))) {
          window.BixdoGifts.sync().catch(() => {});
        }
      } catch (error) {
        if (confirmed) this.status(this.copy.cartRefresh, true);
        else {
          // 网络超时可能发生在服务端已写入之后：禁用再次提交，要求先检查购物车。
          this.uncertain = true;
          this.status(this.copy.uncertain, true);
        }
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        if (this.isConnected) this.update();
      }
    }
  }
  customElements.define('bixdo-product', BixdoProduct);
})();
